import React from "react";
import {
    ArrowLeftIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    CalendarIcon,
    ArchiveBoxIcon,
    EllipsisVerticalIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    ListBulletIcon,
    XCircleIcon,
    ClockIcon,
    CheckCircleIcon,
    QrCodeIcon,
    Squares2X2Icon,
    ClipboardIcon
} from "@heroicons/react/24/outline";
import Loader from "../../../components/common/Loader";

const isBookProduct = (item) => {
    if (!item) return false;
    const type = (item.product_type || item.type || "").toLowerCase();
    if (["subscription", "membership", "giftcard", "gift_card", "event", "event_ticket"].includes(type)) {
        return false;
    }
    const name = (item.name || item.title || "").toLowerCase();
    const nonBookRx = /\b(membership|gift\s*card|subscription|event|plan)\b/;
    if (nonBookRx.test(name)) {
        return false;
    }
    return true;
};


export default function OrdersList({
    mode,
    handleAllOrdersClick,
    handleBackClick,
    orders,
    orderQuery,
    setOrderQuery,
    debouncedOrderQuery,
    setDebouncedOrderQuery,
    allOrdersQuery,
    setAllOrdersQuery,
    debouncedAllOrdersQuery,
    setDebouncedAllOrdersQuery,
    loadingOrders,
    loadingDetails,
    loadingMoreOrders,
    statusCounts,
    countsLoaded,
    listContainerRef,
    activeStatusFilter,
    setActiveStatusFilter,
    selectedOrder,
    selectOrder,
    activeLeftDropdown,
    setActiveLeftDropdown,
    actionSubmitting,
    setDamageRemoveFromOrder,
    handleCardAction,
    handleStartScanReplace,
    packedItems = []
}) {



    const getStatusStyles = (status, isSelected) => {
        const s = (status || "").toLowerCase();

        let cardBorder = "border-gray-200";
        let bgClass = "bg-white hover:bg-gray-50/50";
        let accentBar = "bg-gray-300";
        let badgeClass = "bg-gray-50 text-gray-700 border border-gray-205";
        let iconColor = "text-gray-500 bg-gray-50";
        let StatusIcon = ClockIcon;

        if (s === "cancelled" || s === "trash" || s === "failed") {
            accentBar = "bg-red-500";
            badgeClass = "bg-red-50 text-red-600 border border-red-150";
            iconColor = "text-red-500 bg-red-50 border border-red-100";
            StatusIcon = XCircleIcon;
        } else if (s === "pending" || s === "on-hold" || s === "on_hold") {
            accentBar = "bg-amber-500";
            badgeClass = "bg-amber-50 text-amber-700 border border-amber-200";
            iconColor = "text-amber-600 bg-amber-50 border border-amber-100";
            StatusIcon = ClockIcon;
        } else if (s === "completed") {
            accentBar = "bg-green-500";
            badgeClass = "bg-green-50 text-green-700 border border-green-200";
            iconColor = "text-green-500 bg-green-50 border border-green-100";
            StatusIcon = CheckCircleIcon;
        } else {
            accentBar = "bg-blue-500";
            badgeClass = "bg-blue-50 text-blue-700 border border-blue-200";
            iconColor = "text-blue-500 bg-blue-50 border border-blue-100";
            StatusIcon = ArrowPathIcon;
        }

        if (isSelected) {
            cardBorder = "border-blue-400 ring-1 ring-blue-400";
            bgClass = "bg-blue-50/40";
        }

        return { cardBorder, bgClass, accentBar, badgeClass, iconColor, StatusIcon };
    };

    const filterCards = [
        { id: "all", label: "All Orders", count: statusCounts.all, icon: ListBulletIcon, color: "blue" },
        { id: "cancelled", label: "Cancelled", count: statusCounts.cancelled, icon: XCircleIcon, color: "red" },
        { id: "pending", label: "Pending", count: statusCounts.pending, icon: ClockIcon, color: "amber" },
        { id: "processing", label: "Processing", count: statusCounts.processing, icon: ArrowPathIcon, color: "indigo" },
        { id: "completed", label: "Completed", count: statusCounts.completed, icon: CheckCircleIcon, color: "green" },
    ];

    return (
        <div className="w-[28%] min-w-[300px] border-r border-gray-200 flex flex-col bg-gray-50/30">
            {/* SEARCH AND ALL ORDERS BAR */}
            <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3 text-left">
                {mode === "ALL_ORDERS" && (
                    <div className="flex items-center">
                        <button
                            onClick={handleBackClick}
                            className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs font-bold text-gray-700 bg-white shadow-xs cursor-pointer"
                        >
                            <ArrowLeftIcon className="h-3.5 w-3.5 stroke-[2.5]" />
                            <span>Back</span>
                        </button>
                    </div>
                )}

                <div className="flex items-center gap-3 w-full">
                    {mode === "ALL_ORDERS" ? (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                setDebouncedAllOrdersQuery(allOrdersQuery.trim());
                            }}
                            className="flex-1 relative"
                        >
                            <input
                                value={allOrdersQuery}
                                onChange={(e) => setAllOrdersQuery(e.target.value)}
                                placeholder="Search Order ID, Name, Email"
                                className="w-full border border-gray-200 rounded-xl pl-10 pr-10 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none shadow-sm transition-all placeholder:text-gray-400 bg-white"
                            />
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                                <MagnifyingGlassIcon className="w-5 h-5" />
                            </span>
                            {allOrdersQuery && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setAllOrdersQuery("");
                                        setDebouncedAllOrdersQuery("");
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors cursor-pointer"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            )}
                        </form>
                    ) : (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                setDebouncedOrderQuery(orderQuery.trim());
                            }}
                            className="flex-1 relative"
                        >
                            <input
                                value={orderQuery}
                                onChange={(e) => setOrderQuery(e.target.value)}
                                placeholder="Search Order Number"
                                className="w-full border border-gray-200 rounded-xl pl-10 pr-10 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none shadow-sm transition-all placeholder:text-gray-400 bg-white"
                            />
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                                <MagnifyingGlassIcon className="w-5 h-5" />
                            </span>
                            {orderQuery && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOrderQuery("");
                                        setDebouncedOrderQuery("");
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors cursor-pointer"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            )}
                        </form>
                    )}
                    <button
                        onClick={handleAllOrdersClick}
                        className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-semibold transition-colors shadow-sm bg-white whitespace-nowrap cursor-pointer ${mode === "ALL_ORDERS"
                            ? "border-blue-300 bg-blue-50/20 text-blue-700 font-bold"
                            : "border-gray-200 text-gray-700 hover:bg-gray-50"
                            }`}
                    >
                        <Squares2X2Icon className="w-4 h-4 text-gray-700" />
                        <span>All Orders</span>
                    </button>
                </div>
            </div>

            {/* Mode 1: INITIAL */}
            {mode === "INITIAL" && (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-gray-400 bg-white">
                    <ClipboardIcon className="w-16 h-16 text-gray-200 stroke-[1.2] mb-4" />
                    <h3 className="text-sm font-bold text-gray-500">Search an order number</h3>
                    <p className="text-xs text-gray-400 mt-1">to view books and details</p>
                </div>
            )}

            {/* Mode 2: SEARCH_RESULT */}
            {mode === "SEARCH_RESULT" && (
                <div className="flex-1 flex flex-col bg-white overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 text-left bg-gray-50/40">
                        <span className="font-bold text-sm text-gray-800">
                            Books in Order #{selectedOrder?.order_id || orderQuery}
                        </span>
                        {selectedOrder?.items && (
                            <span className="bg-blue-600 text-white rounded-full px-2.5 py-0.5 text-[10px] font-extrabold">
                                {selectedOrder.items.length}
                            </span>
                        )}
                    </div>

                    {/* Scrollable Items List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {loadingDetails ? (
                            <div className="flex justify-center pt-8">
                                <Loader />
                            </div>
                        ) : !selectedOrder?.items || selectedOrder.items.length === 0 ? (
                            <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-xl border border-gray-150 shadow-xs">
                                No books found in this order
                            </div>
                        ) : (
                            selectedOrder.items.map((item, idx) => {
                                const itemImage = item.image || item.img || "https://via.placeholder.com/80x120?text=Cover";
                                const itemTitle = item.name || item.title || `Item #${idx + 1}`;
                                const isBook = isBookProduct(item);
                                const isIssue = (item.issue_or_return || "issue") === "issue";
                                const isPacked = packedItems?.some(p => String(p.product_id) === String(item.product_id));

                                const itemId = item.product_id || idx;
                                return (
                                    <div
                                        key={itemId}
                                        className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl p-3 bg-white hover:bg-gray-50/30 transition-all text-left"
                                    >
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <img
                                                src={itemImage}
                                                alt={itemTitle}
                                                className="w-16 h-24 object-cover rounded border border-gray-150 flex-shrink-0"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-bold text-sm text-gray-900 truncate">
                                                        {itemTitle}
                                                    </h4>
                                                    {isPacked && (
                                                        <span className="bg-green-100 text-green-800 text-[9px] font-extrabold px-1.5 py-0.5 rounded uppercase">
                                                            Packed
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-blue-600 font-semibold mt-0.5">
                                                    {item.credits} Credits
                                                </p>
                                                <p className="text-[11px] text-gray-500 uppercase mt-1 font-medium">
                                                    {item.issue_or_return}
                                                </p>
                                                <p className="text-[11px] text-gray-500 mt-1 font-medium">
                                                    {isBook
                                                        ? `ID: ${item.isbn || item.sku || item.product_id || "N/A"}`
                                                        : `Qty: ${item.quantity || 1}`}
                                                </p>
                                            </div>
                                        </div>
                                        {isIssue ? (
                                            <div
                                                className="flex flex-col gap-2.5 items-center justify-center flex-shrink-0 border-l border-gray-200 pl-4 relative self-stretch"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="relative">
                                                    <button
                                                        disabled={actionSubmitting}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveLeftDropdown(prev => prev === itemId ? null : itemId);
                                                        }}
                                                        className="w-9 h-9 border border-gray-205 rounded-xl bg-white shadow-sm text-gray-700 flex items-center justify-center cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#F8FAFC] hover:-translate-y-[1px] hover:border-gray-300 disabled:opacity-50"
                                                        title="More Actions"
                                                    >
                                                        <EllipsisVerticalIcon className="h-5 w-5 stroke-[2] text-gray-700" />
                                                    </button>

                                                    {activeLeftDropdown === itemId && (
                                                        <div
                                                            className="absolute right-full mr-2 top-0 w-36 rounded-xl shadow-lg bg-white border border-gray-205 z-[100] py-1 divide-y divide-gray-100 text-xs font-semibold"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {!isPacked && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActiveLeftDropdown(null);
                                                                        handleCardAction(item, "issued");
                                                                    }}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-green-50 text-green-700 font-semibold transition-colors text-left cursor-pointer"
                                                                >
                                                                    <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
                                                                    <span>Mark as Issued</span>
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveLeftDropdown(null);
                                                                    setDamageRemoveFromOrder(true);
                                                                    handleCardAction(item, "damaged");
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600 font-semibold transition-colors text-left cursor-pointer"
                                                            >
                                                                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 text-red-500" />
                                                                <span>Damaged</span>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveLeftDropdown(null);
                                                                    handleCardAction(item, "lost");
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600 font-semibold transition-colors text-left cursor-pointer"
                                                            >
                                                                <XCircleIcon className="h-4 w-4 flex-shrink-0 text-red-500" />
                                                                <span>Lost</span>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveLeftDropdown(null);
                                                                    handleCardAction(item, "restock");
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-gray-700 font-semibold transition-colors text-left cursor-pointer"
                                                            >
                                                                <ArrowPathIcon className="h-4 w-4 flex-shrink-0 text-gray-500" />
                                                                <span>Restock</span>
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveLeftDropdown(null);
                                                                    handleCardAction(item, "missing");
                                                                }}
                                                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-amber-50 text-amber-700 font-semibold transition-colors text-left cursor-pointer"
                                                            >
                                                                <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-amber-600" />
                                                                <span>Missing</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                <button
                                                    disabled={actionSubmitting}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStartScanReplace(item);
                                                    }}
                                                    className="w-9 h-9 border border-gray-250 rounded-xl bg-white shadow-sm text-blue-600 flex items-center justify-center cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#F8FAFC] hover:-translate-y-[1px] hover:border-gray-300 disabled:opacity-50"
                                                    title="Scan / Copy ID"
                                                >
                                                    <QrCodeIcon className="h-5 w-5 stroke-[2] text-gray-700" />
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* Mode 3: ALL_ORDERS */}
            {mode === "ALL_ORDERS" && (
                <>
                    <div className="px-4 py-3 flex flex-wrap gap-2 overflow-x-auto no-scrollbar border-b border-gray-200 bg-white">
                        {filterCards.map((card) => {
                            const CardIcon = card.icon;
                            const isActive = activeStatusFilter === card.id;

                            let activeStyles = "";
                            let inactiveStyles = "bg-white border-gray-200 text-gray-700 hover:bg-gray-50";
                            let badgeStyles = "";

                            if (card.color === "blue") {
                                activeStyles = "bg-blue-50/70 border-blue-300 text-blue-700";
                                badgeStyles = isActive ? "bg-blue-500 text-white" : "bg-blue-100 text-blue-800";
                            } else if (card.color === "red") {
                                activeStyles = "bg-red-50/70 border-red-300 text-red-700";
                                badgeStyles = isActive ? "bg-red-500 text-white" : "bg-red-100 text-red-800";
                            } else if (card.color === "amber") {
                                activeStyles = "bg-amber-50/70 border-amber-300 text-amber-700";
                                badgeStyles = isActive ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-800";
                            } else if (card.color === "indigo") {
                                activeStyles = "bg-indigo-50/70 border-indigo-300 text-indigo-700";
                                badgeStyles = isActive ? "bg-indigo-500 text-white" : "bg-indigo-100 text-indigo-800";
                            } else if (card.color === "green") {
                                activeStyles = "bg-green-50/70 border-green-300 text-green-700";
                                badgeStyles = isActive ? "bg-green-500 text-white" : "bg-green-100 text-green-800";
                            }

                            return (
                                <button
                                    key={card.id}
                                    onClick={() => setActiveStatusFilter(card.id)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer shadow-xs ${isActive ? activeStyles : inactiveStyles}`}
                                >
                                    <CardIcon className="w-4 h-4 flex-shrink-0" />
                                    <span>{card.label}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${badgeStyles}`}>
                                        {countsLoaded ? card.count : "·"}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div ref={listContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                        {loadingOrders && orders.length === 0 && (
                            <div className="flex justify-center pt-8"><Loader /></div>
                        )}

                        {!loadingOrders && orders.length === 0 && (
                            <div className="text-center py-10 text-gray-400 text-sm bg-white rounded-xl border border-gray-150 shadow-xs">No orders found</div>
                        )}

                        {orders.map((order) => {
                            const orderId = order.order_id;
                            const isSelected = !!selectedOrder && selectedOrder.order_id === orderId;
                            const orderStatus = order.status || "processing";
                            const dateCreated = order.date_created || "N/A";
                            const itemsCount = order.item_count || 0;
                            const totalAmount = order.total || 0;
                            const { cardBorder, bgClass, accentBar, badgeClass, iconColor, StatusIcon } = getStatusStyles(orderStatus, isSelected);

                            return (
                                <div
                                    key={orderId}
                                    onClick={() => selectOrder(order)}
                                    className={`relative flex items-center justify-between gap-4 border rounded-2xl p-4 cursor-pointer transition-all duration-200 w-full overflow-hidden ${cardBorder} ${bgClass}`}
                                >
                                    <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${accentBar}`} />

                                    <div className="flex-1 min-w-0 flex gap-3 text-left">
                                        <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${iconColor}`}>
                                            <StatusIcon className="w-5 h-5 stroke-[2]" />
                                        </div>

                                        <div className="flex-1 min-w-0 space-y-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-bold text-sm text-gray-900">
                                                    Order #{orderId}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-wider ${badgeClass}`}>
                                                    {orderStatus}
                                                </span>
                                            </div>

                                            <div className="space-y-1 text-xs text-gray-500 font-medium">
                                                <div className="flex items-center gap-1.5">
                                                    <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                                                    <span>Date: <span className="text-gray-700 font-semibold">{dateCreated.split(" ")[0]}</span></span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <ArchiveBoxIcon className="w-3.5 h-3.5 text-gray-400" />
                                                    <span>Items: <span className="text-gray-700 font-semibold">{itemsCount} book{itemsCount !== 1 ? "s" : ""}</span></span>
                                                </div>
                                            </div>

                                            <div className="pt-2 border-t border-gray-100 flex justify-between items-center text-xs">
                                                <span className="text-gray-400 font-medium">Total:</span>
                                                <span className={`font-bold text-sm ${orderStatus === "cancelled" || orderStatus === "trash" || orderStatus === "failed" ? "text-red-600" :
                                                    orderStatus === "pending" || orderStatus === "on-hold" || orderStatus === "on_hold" ? "text-amber-600" :
                                                        orderStatus === "completed" ? "text-green-600" : "text-blue-600"
                                                    }`}>₹{totalAmount}</span>
                                            </div>
                                        </div>
                                    </div>


                                </div>
                            );
                        })}

                        {loadingMoreOrders && (
                            <div className="flex justify-center py-4"><Loader /></div>
                        )}
                    </div>

                    <div className="p-3 bg-white border-t border-gray-200 text-center text-xs font-bold text-gray-500">
                        {orders.length > 0 && `Showing ${orders.length} orders`}
                    </div>
                </>
            )}
        </div>
    );
}
